import { useId, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ArrowLeft, KeyRound, LoaderCircle } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BRAND } from '@/config/brand';
import { cn } from '@/lib/utils';
import { api, ApiClientError } from '../../api/client';
import { endSession, PUBLIC_QUERY_META, useSignOut } from '../../api/query';
import { Notice } from '@/components/Notice';
import { notify } from '@/lib/toast';
import { HINT } from './parts';

/**
 * The two doors into the product: signing in, and accepting an invitation. There is deliberately no third — the
 * company has no public registration, and the invitation page never lets the browser decide which account is being
 * changed: the email it shows comes from the server's reading of the token and is not editable, and the only thing
 * it ever sends back is a password. An access reset hands out a temporary password instead of a link (hotfix
 * 2026-09-28); the session it opens sees only `ForcedPasswordPage`.
 *
 * The token itself lives in the URL and in nothing else. It is never written to storage, never logged, and
 * never put into a query key that outlives the page.
 */

type LinkKind = 'invite';

/** Local submission state for one form. The password never leaves React state, and is never persisted. */
function useAuthSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(work: () => Promise<void>) {
    // A second click while the first is in flight is the commonest way to create a duplicate account action.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : 'Không thể lưu. Hãy thử lại.');
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

/**
 * The HyperTek prototype's sign-in layout (h-prototype `routes/App/login.tsx`): one centred column, the logo
 * over the product's name and sub-label, then the form on a card. The product keeps its own page title as
 * the `h1` inside the card — tests, the e2e suite and a screen reader's heading list all find the page by it —
 * so the name under the logo is plain text. The image already carries that name as its accessible name, and
 * the visible copy is hidden from assistive technology so it is heard once, not twice.
 */
export function AuthLayout({ title, subtitle, footer, wide = false, children }: {
  title: string;
  /** A line straight under the title, part of the heading block (the consent screen's "dưới tên …"). */
  subtitle?: ReactNode;
  /** A quiet line under the card's content, on its own tinted strip. */
  footer?: ReactNode;
  /** A wider column for a page with more to read than a form (the OAuth consent screen). */
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[hsl(var(--h-background))] p-4">
      <div className={cn('w-full', wide ? 'max-w-md' : 'max-w-sm')}>
        <header className="mb-8 flex flex-col items-center">
          <img src={BRAND.logoSrc} alt={BRAND.name} width={56} height={56} className="mb-3 object-contain" />
          <p aria-hidden="true" className="text-xl font-bold text-[hsl(var(--h-card-foreground))]">{BRAND.name}</p>
          <p className="text-sm text-[hsl(var(--h-muted-foreground))]">{BRAND.subtitle}</p>
        </header>
        <main id="noi-dung-chinh" className="h-card overflow-hidden">
          <div className="p-6">
            <h1 className="text-lg font-semibold tracking-tight text-[hsl(var(--h-card-foreground))] wrap-break-word">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-[hsl(var(--h-muted-foreground))]">{subtitle}</p>}
            <div className="mt-6">{children}</div>
          </div>
          {footer && (
            <div className="flex gap-2.5 border-t border-[hsl(var(--h-border))] bg-[hsl(var(--h-muted)/0.5)] px-6 py-4 text-sm text-muted-foreground">
              {footer}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export function LoginForm({ onSubmit }: { onSubmit: (email: string, password: string) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const state = useAuthSubmit();
  const id = useId();
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => { event.preventDefault(); void state.run(() => onSubmit(email, password)); }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-email`}>Email</Label>
        <Input
          id={`${id}-email`} type="email" autoComplete="username" required
          value={email} onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-password`}>Mật khẩu</Label>
        <Input
          id={`${id}-password`} type="password" autoComplete="current-password" required
          value={password} onChange={(event) => setPassword(event.target.value)}
        />
      </div>
      {state.error && <Notice tone="error" role="alert">{state.error}</Notice>}
      <Button type="submit" className="mt-2 w-full" disabled={state.busy}>Đăng nhập</Button>
    </form>
  );
}

type LinkFormProps = { email: string; name?: string; onSubmit: (password: string) => Promise<void> };

function PasswordLinkForm({ email, name, onSubmit }: LinkFormProps) {
  const [password, setPassword] = useState('');
  const state = useAuthSubmit();
  const id = useId();
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void state.run(async () => {
          // The server enforces the same rule; refusing here saves a round trip and a failed attempt record.
          if ([...password].length < 6 || [...password].length > 128) {
            throw new ApiClientError(422, 'VALIDATION', 'Mật khẩu cần 6–128 ký tự');
          }
          await onSubmit(password);
        });
      }}
    >
      {name && (
        <p className="text-sm text-muted-foreground">
          Tài khoản của <span className="font-semibold text-[hsl(var(--h-card-foreground))]">{name}</span>
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-email`}>Email</Label>
        <Input id={`${id}-email`} value={email} readOnly autoComplete="username" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-password`}>Mật khẩu mới</Label>
        <Input
          id={`${id}-password`} type="password" autoComplete="new-password" required
          value={password} onChange={(event) => setPassword(event.target.value)}
        />
        <p className={HINT}>Dùng 6–128 ký tự. Không đặt lại mật khẩu bạn đang dùng ở nơi khác.</p>
      </div>
      {state.error && <Notice tone="error" role="alert">{state.error}</Notice>}
      <Button type="submit" className="mt-2 w-full" disabled={state.busy}>Lưu mật khẩu</Button>
    </form>
  );
}

export function AcceptInviteForm(props: LinkFormProps) { return <PasswordLinkForm {...props} />; }

function linkPath(_kind: LinkKind, token: string): string {
  return `/api/auth/invitations/${encodeURIComponent(token)}`;
}

export function loadLink(kind: LinkKind, token: string): Promise<{ email: string; name: string }> {
  return api(linkPath(kind, token));
}

export function completeLink(kind: LinkKind, token: string, password: string): Promise<void> {
  return api(`${linkPath(kind, token)}/accept`, { method: 'POST', body: { password } });
}

const LINK_TITLE: Record<LinkKind, string> = { invite: 'Tạo mật khẩu' };

function LinkPage({ kind }: { kind: LinkKind }) {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // gcTime 0: the answer belongs to one use of one link and must not outlive this page in any cache.
  const link = useQuery({
    queryKey: ['auth-link', kind, token],
    queryFn: () => loadLink(kind, token),
    retry: false,
    gcTime: 0,
    // A refusal here is the link's answer, never a sign that a session in this tab ended.
    meta: PUBLIC_QUERY_META,
  });

  async function save(password: string) {
    await completeLink(kind, token, password);
    queryClient.removeQueries({ queryKey: ['auth-link', kind, token] });
    await navigate('/login', { replace: true });
  }

  return (
    <AuthLayout title={LINK_TITLE[kind]}>
      {link.isPending && (
        <p className="flex items-center gap-2.5 text-muted-foreground">
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          Đang mở liên kết…
        </p>
      )}
      {link.isError && (
        <div className="flex flex-col gap-4">
          <Notice tone="error" role="alert">
            {link.error instanceof ApiClientError
              ? link.error.message
              : 'Liên kết không dùng được. Hãy đề nghị quản trị viên cấp liên kết mới.'}
          </Notice>
          <p className={HINT}>Hãy đề nghị quản trị viên cấp liên kết mới qua kênh riêng đã xác minh.</p>
          <Link to="/login" className={cn(buttonVariants({ variant: 'outline' }), 'w-full')}>
            <ArrowLeft aria-hidden="true" />
            Về trang đăng nhập
          </Link>
        </div>
      )}
      {link.isSuccess && <AcceptInviteForm email={link.data.email} name={link.data.name} onSubmit={save} />}
    </AuthLayout>
  );
}

export function InvitePage() { return <LinkPage kind="invite" />; }

/**
 * Where sign-in may lead besides `/` (D28): only the consent screen of one OAuth request, or the confirm page of one
 * pending action an AI connection asked for (S2 §11-4: the link a client hands its owner), each by its exact shape.
 * Anything else — another origin (`//host`), a query, a traversal, any other page — is ignored, so `next` can never be
 * used to send a freshly signed-in person somewhere a link author chose.
 */
const ALLOWED_NEXT = /^\/connect\/(?:authorize|actions)\/[0-9a-f-]{36}$/;

export function safeNext(next: string | null): string {
  return next !== null && ALLOWED_NEXT.test(next) ? next : '/';
}

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  return (
    <AuthLayout
      title="Đăng nhập"
      footer={(
        <>
          <KeyRound className="mt-0.5 size-4 shrink-0 self-start" aria-hidden="true" />
          <p>Quên mật khẩu? Đề nghị quản trị viên cấp mật khẩu tạm.</p>
        </>
      )}
    >
      <LoginForm
        onSubmit={async (email, password) => {
          await api('/api/auth/login', { method: 'POST', body: { email, password } });
          // A new session starts from nothing (authz audit D-F1): whoever this tab held before — a session that died
          // without "Đăng xuất", or someone still signed in when this page was opened — leaves no cached answer,
          // no board state and no card id behind. The principal goes too, so the shell mounts on a fresh read of
          // `/api/me` rather than on the `null` that turned the visitor away and would bounce them back here.
          endSession(queryClient);
          await navigate(next, { replace: true });
        }}
      />
    </AuthLayout>
  );
}

export const PASSWORD_CHANGED_TOAST = 'Đã đổi mật khẩu. Hãy đăng nhập lại bằng mật khẩu mới.';

/**
 * The new password and its confirmation, for a session opened with a temporary password. The server checks the
 * same length rule and that it differs from the temporary one; its field message is shown under the field.
 */
export function ForcedPasswordForm({ onSubmit }: { onSubmit: (newPassword: string) => Promise<void> }) {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const id = useId();

  async function send() {
    if (busy) return;
    setError(null); setFieldError(null);
    if ([...next].length < 6 || [...next].length > 128) { setFieldError('Mật khẩu cần 6–128 ký tự'); return; }
    if (next !== confirm) { setError('Hai ô mật khẩu mới chưa giống nhau'); return; }
    setBusy(true);
    try {
      await onSubmit(next);
    } catch (cause) {
      const field = cause instanceof ApiClientError ? cause.fields?.newPassword : undefined;
      if (field) setFieldError(field);
      else setError(cause instanceof ApiClientError ? cause.message : 'Không thể lưu. Hãy thử lại.');
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={(event) => { event.preventDefault(); void send(); }}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-next`}>Mật khẩu mới</Label>
        <Input
          id={`${id}-next`} type="password" autoComplete="new-password" required
          aria-invalid={fieldError ? true : undefined}
          value={next} onChange={(event) => setNext(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-confirm`}>Xác nhận mật khẩu mới</Label>
        <Input
          id={`${id}-confirm`} type="password" autoComplete="new-password" required
          value={confirm} onChange={(event) => setConfirm(event.target.value)}
        />
      </div>
      {(fieldError ?? error) && <Notice tone="error" role="alert">{fieldError ?? error}</Notice>}
      <Button type="submit" className="mt-2 w-full" disabled={busy}>Đổi mật khẩu</Button>
    </form>
  );
}

/**
 * What the shell renders, alone, for a session opened with a temporary password: no navigation, no data, only the
 * change. The server ends every session on success, this one included, so the tab forgets it and goes to sign-in.
 */
export function ForcedPasswordPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  return (
    <AuthLayout title="Đổi mật khẩu">
      <ForcedPasswordForm
        onSubmit={async (newPassword) => {
          try {
            await api('/api/account/password/forced', { method: 'POST', body: { newPassword } });
          } catch (cause) {
            // A dead session (401) or a newer reset that overtook this one (409) can never succeed from this form:
            // the tab forgets the session and goes to sign-in, carrying the server's reason.
            if (!(cause instanceof ApiClientError) || (cause.status !== 401 && cause.status !== 409)) throw cause;
            endSession(queryClient);
            await navigate('/login', { replace: true });
            notify.error(cause.message);
            return;
          }
          endSession(queryClient);
          await navigate('/login', { replace: true });
          notify.success(PASSWORD_CHANGED_TOAST);
        }}
      />
      <Button
        type="button" variant="ghost" className="mt-2 w-full"
        onClick={() => { void signOut().then(() => navigate('/login', { replace: true })); }}
      >
        Đăng xuất
      </Button>
    </AuthLayout>
  );
}
